import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowRight, MessageCircle, CalendarClock, Mail, MapPin, Phone, ShieldCheck } from 'lucide-react';
import { getCalApi } from '@calcom/embed-react';
import SEOHead from '../components/UI/SEOHead';
import { SITE_CONFIG, waLink, calLink, CAL_UI_CONFIG } from '../config';
import Reveal from '../components/UI/Reveal';
import Magnetic from '../components/UI/Magnetic';

const ContactPage: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    (async () => {
      const cal = await getCalApi();
      cal('ui', { ...CAL_UI_CONFIG, hideEventTypeDetails: false });
    })();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      <SEOHead
        title={t('contact_page.seo_title', 'Contacto')}
        description={t('contact_page.seo_desc', 'Agenda una demo de 15 minutos con RurayHUB. Escríbenos por WhatsApp o elige tu horario.')}
        path="/contacto"
      />

      <style>{`
        .contact-hero {
          padding: clamp(7rem, 12vh, 10rem) 2rem clamp(3rem, 5vh, 5rem);
          text-align: center;
        }
        .contact-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
          max-width: 960px;
          margin: 0 auto;
          padding: 0 1.5rem clamp(4rem, 8vh, 7rem);
        }
        .contact-card {
          background: rgba(255,255,255,0.025);
          border: 1px solid var(--border-light);
          border-radius: 20px;
          padding: clamp(1.75rem, 3vw, 2.5rem);
          text-align: center;
          transition: border-color 0.3s, transform 0.3s;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }
        .contact-card:hover {
          border-color: var(--border-lighter);
          transform: translateY(-4px);
        }
        .contact-icon-wrap {
          width: 56px;
          height: 56px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .contact-card-title {
          font-family: var(--font-heading);
          font-size: 1.15rem;
          font-weight: 700;
          color: var(--text-primary);
          margin: 0;
        }
        .contact-card-desc {
          font-size: 0.92rem;
          color: var(--text-secondary);
          line-height: 1.55;
          margin: 0;
        }
        .contact-card-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 0.75rem 1.5rem;
          border-radius: 12px;
          font-weight: 700;
          font-family: var(--font-heading);
          font-size: 0.9rem;
          text-decoration: none;
          cursor: pointer;
          border: none;
          transition: transform 0.2s, box-shadow 0.2s;
          margin-top: auto;
        }
        .contact-card-btn:hover {
          transform: translateY(-2px);
        }
        .contact-info-section {
          max-width: 720px;
          margin: 0 auto;
          padding: 0 1.5rem clamp(4rem, 8vh, 7rem);
        }
        .contact-info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.25rem;
        }
        .contact-info-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 1rem 1.25rem;
          background: rgba(255,255,255,0.02);
          border: 1px solid var(--border-light);
          border-radius: 14px;
        }
        .contact-info-item-text {
          font-size: 0.9rem;
          color: var(--text-secondary);
        }
        .contact-info-item-text strong {
          display: block;
          color: var(--text-primary);
          font-size: 0.82rem;
          font-weight: 700;
          margin-bottom: 2px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
      `}</style>

      {/* Hero */}
      <section className="contact-hero">
        <Reveal>
          <motion.span
            style={{
              display: 'inline-block',
              fontSize: '0.72rem',
              fontWeight: 800,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'var(--accent-teal)',
              marginBottom: '1.2rem',
              padding: '6px 14px',
              borderRadius: 999,
              border: '1px solid rgba(0,229,209,0.25)',
              background: 'rgba(0,229,209,0.06)',
            }}
          >
            {t('contact_page.eyebrow', 'Contacto')}
          </motion.span>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: 'clamp(2rem, 4.5vw, 3.5rem)',
            fontWeight: 300,
            color: 'var(--text-primary)',
            margin: '0 0 1rem',
            lineHeight: 1.12,
            letterSpacing: '-0.03em',
          }}>
            {t('contact_page.title_start', '¿Listo para ')}
            <span className="grad-text" style={{ fontWeight: 800 }}>
              {t('contact_page.title_highlight', 'llenar tu agenda?')}
            </span>
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p style={{
            color: 'var(--text-secondary)',
            maxWidth: 560,
            margin: '0 auto',
            fontSize: '1.08rem',
            lineHeight: 1.6,
          }}>
            {t('contact_page.subtitle', 'Elige cómo prefieres contactarnos. Respondemos en minutos.')}
          </p>
        </Reveal>
      </section>

      {/* Contact Options */}
      <div className="contact-grid">
        {/* WhatsApp */}
        <Reveal delay={0}>
          <div className="contact-card">
            <div className="contact-icon-wrap" style={{ background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.25)' }}>
              <MessageCircle size={24} color="#25D366" />
            </div>
            <h2 className="contact-card-title">{t('contact_page.wa_title', 'WhatsApp')}</h2>
            <p className="contact-card-desc">
              {t('contact_page.wa_desc', 'Escríbenos y nuestro agente te atiende al instante. La misma experiencia que vivirán tus pacientes.')}
            </p>
            <Magnetic>
              <a
                href={waLink(SITE_CONFIG.whatsapp.demoMessage)}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-card-btn"
                style={{
                  background: 'linear-gradient(135deg, #25D366, #14a85a)',
                  color: '#fff',
                  boxShadow: '0 10px 30px -10px rgba(37,211,102,0.5)',
                }}
              >
                {t('contact_page.wa_btn', 'Escribir por WhatsApp')}
                <ArrowRight size={16} />
              </a>
            </Magnetic>
          </div>
        </Reveal>

        {/* Cal.com */}
        <Reveal delay={0.08}>
          <div className="contact-card">
            <div className="contact-icon-wrap" style={{ background: 'rgba(0,229,209,0.1)', border: '1px solid rgba(0,229,209,0.25)' }}>
              <CalendarClock size={24} color="var(--accent-teal)" />
            </div>
            <h2 className="contact-card-title">{t('contact_page.cal_title', 'Agendar demo')}</h2>
            <p className="contact-card-desc">
              {t('contact_page.cal_desc', 'Elige tu horario y recibe una videollamada de 15 minutos en Google Meet con nuestro equipo.')}
            </p>
            <Magnetic>
              <button
                data-cal-link={calLink()}
                data-cal-config='{"theme":"dark"}'
                className="contact-card-btn"
                style={{
                  background: 'var(--accent-teal)',
                  color: '#00201d',
                  boxShadow: '0 10px 30px -10px rgba(0,229,209,0.5)',
                }}
              >
                {t('contact_page.cal_btn', 'Elegir horario')}
                <CalendarClock size={16} />
              </button>
            </Magnetic>
          </div>
        </Reveal>

        {/* Email */}
        <Reveal delay={0.16}>
          <div className="contact-card">
            <div className="contact-icon-wrap" style={{ background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.25)' }}>
              <Mail size={24} color="var(--accent-purple)" />
            </div>
            <h2 className="contact-card-title">{t('contact_page.email_title', 'Correo electrónico')}</h2>
            <p className="contact-card-desc">
              {t('contact_page.email_desc', 'Para consultas formales, alianzas o soporte técnico. Respondemos en menos de 24 horas.')}
            </p>
            <Magnetic>
              <a
                href={`mailto:${SITE_CONFIG.email}`}
                className="contact-card-btn"
                style={{
                  background: 'rgba(139,92,246,0.12)',
                  color: 'var(--accent-purple)',
                  border: '1px solid rgba(139,92,246,0.3)',
                }}
              >
                {SITE_CONFIG.email}
                <Mail size={16} />
              </a>
            </Magnetic>
          </div>
        </Reveal>
      </div>

      {/* Info */}
      <div className="contact-info-section">
        <Reveal>
          <div className="contact-info-grid">
            <div className="contact-info-item">
              <MapPin size={18} color="var(--accent-teal)" style={{ flexShrink: 0 }} />
              <div className="contact-info-item-text">
                <strong>{t('contact_page.location_label', 'Ubicación')}</strong>
                Ecuador · Toda Latinoamérica
              </div>
            </div>
            <div className="contact-info-item">
              <Phone size={18} color="var(--accent-teal)" style={{ flexShrink: 0 }} />
              <div className="contact-info-item-text">
                <strong>{t('contact_page.phone_label', 'Teléfono')}</strong>
                +593 987 119 183
              </div>
            </div>
            <div className="contact-info-item">
              <ShieldCheck size={18} color="var(--accent-teal)" style={{ flexShrink: 0 }} />
              <div className="contact-info-item-text">
                <strong>{t('contact_page.response_label', 'Respuesta')}</strong>
                {t('contact_page.response_value', 'En minutos por WhatsApp')}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </motion.div>
  );
};

export default ContactPage;
